import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { setSessionProfileId } from "../context/OnboardingContext";
import { Toaster } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/profiles/lookup/by-email`, {
        params: { email: email.trim().toLowerCase() },
      });
      setSessionProfileId(res.data.id);
      navigate("/profile");
    } catch (err) {
      if (err?.response?.status === 404) {
        setError(
          "Wir finden kein Konto mit dieser E-Mail-Adresse. Möchtest du dich registrieren?"
        );
      } else {
        setError("Etwas ist schiefgelaufen. Bitte erneut versuchen.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-body text-qh-ink">
      <Toaster richColors position="top-center" />
      <header className="border-b border-qh-line">
        <div className="max-w-xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0" data-testid="login-logo">
            <img
              src="/assets/qhearts-logo.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>
          <Link
            to="/onboarding"
            className="text-sm font-medium text-qh-rose hover:text-qh-rosedark"
          >
            Registrieren
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <h1 className="font-display text-4xl font-medium tracking-tight leading-tight">
            Willkommen zurück.
          </h1>
          <p className="mt-3 text-qh-slate">
            Gib die E-Mail-Adresse ein, mit der du dich registriert hast.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="deine@email.de"
              className="w-full h-14 px-5 rounded-2xl border border-qh-line bg-white text-lg focus:outline-none focus:border-qh-rose focus:ring-2 focus:ring-qh-rose/20"
              data-testid="login-email"
            />
            {error && (
              <p className="text-sm text-qh-rose" data-testid="login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-qh-rose hover:bg-qh-rosedark text-white font-medium transition-all hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Prüfen…
                </span>
              ) : (
                "Einloggen"
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-qh-line text-center text-sm">
            <span className="text-qh-slate">Noch kein Konto? </span>
            <Link
              to="/onboarding"
              className="font-medium text-qh-rose hover:text-qh-rosedark"
              data-testid="login-to-register"
            >
              Jetzt registrieren
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
